/**
 * ResponsiveTable Component
 * Displays data as a table on desktop and cards on mobile
 */

import { type ReactNode } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import './ResponsiveTable.css';

export interface Column<T> {
  key: string;
  /** V2.I.7 : accepts ReactNode so callers can embed sort indicators,
   *  tooltips, icons, etc. The original `string` form still works. */
  header: ReactNode;
  /** Plain-text header used by mobile cards as the row label. Falls
   *  back to `key` when omitted. Only required when `header` isn't a
   *  string (because cards need a flat label). */
  cardLabel?: string;
  /** Optional click handler on the table header cell (desktop view).
   *  Useful for sortable columns. The header keeps its existing layout. */
  onHeaderClick?: () => void;
  /** Render function for table cell */
  render?: (item: T, index: number) => ReactNode;
  /** Render function for card view (mobile) */
  renderCard?: (item: T, index: number) => ReactNode;
  /** Hide this column on mobile card view */
  hideOnMobile?: boolean;
  /** Show this as the card title */
  isTitle?: boolean;
  /** Show this as the card subtitle */
  isSubtitle?: boolean;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Column width */
  width?: string;
}

export interface ResponsiveTableProps<T> {
  data: T[];
  columns: Column<T>[];
  /** Unique key for each row */
  keyExtractor: (item: T, index: number) => string | number;
  /** Custom card renderer for full control on mobile */
  renderCard?: (item: T, index: number) => ReactNode;
  /** Action buttons for each row */
  actions?: (item: T, index: number) => ReactNode;
  /** Empty state content */
  emptyState?: ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Table caption for accessibility */
  caption?: string;
  /** Breakpoint for switching to cards (default: 768px) */
  breakpoint?: number;
  /** Additional class name */
  className?: string;
  /** On row click handler */
  onRowClick?: (item: T, index: number) => void;
}

export function ResponsiveTable<T>({
  data,
  columns,
  keyExtractor,
  renderCard: customRenderCard,
  actions,
  emptyState,
  isLoading,
  caption,
  breakpoint = 768,
  className = '',
  onRowClick,
}: ResponsiveTableProps<T>) {
  const isMobile = useMediaQuery(`(max-width: ${breakpoint}px)`);

  if (isLoading) {
    return (
      <div className="responsive-table-loading">
        <div className="responsive-table-skeleton" />
        <div className="responsive-table-skeleton" />
        <div className="responsive-table-skeleton" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div className="responsive-table-empty">{emptyState || 'No data'}</div>;
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className={`responsive-table-cards ${className}`}>
        {data.map((item, index) => {
          const key = keyExtractor(item, index);

          // Use custom card renderer if provided
          if (customRenderCard) {
            return (
              <div
                key={key}
                className={`responsive-table-card ${onRowClick ? 'clickable' : ''}`}
                onClick={() => onRowClick?.(item, index)}
              >
                {customRenderCard(item, index)}
              </div>
            );
          }

          // Auto-generate card from columns
          const titleColumn = columns.find(c => c.isTitle);
          const subtitleColumn = columns.find(c => c.isSubtitle);
          const detailColumns = columns.filter(c => !c.isTitle && !c.isSubtitle && !c.hideOnMobile);

          return (
            <div
              key={key}
              className={`responsive-table-card ${onRowClick ? 'clickable' : ''}`}
              onClick={() => onRowClick?.(item, index)}
            >
              <div className="responsive-table-card-header">
                <div className="responsive-table-card-title-group">
                  {titleColumn && (
                    <div className="responsive-table-card-title">
                      {titleColumn.renderCard
                        ? titleColumn.renderCard(item, index)
                        : titleColumn.render
                        ? titleColumn.render(item, index)
                        : String((item as any)[titleColumn.key] ?? '')}
                    </div>
                  )}
                  {subtitleColumn && (
                    <div className="responsive-table-card-subtitle">
                      {subtitleColumn.renderCard
                        ? subtitleColumn.renderCard(item, index)
                        : subtitleColumn.render
                        ? subtitleColumn.render(item, index)
                        : String((item as any)[subtitleColumn.key] ?? '')}
                    </div>
                  )}
                </div>
                {actions && (
                  <div className="responsive-table-card-actions" onClick={e => e.stopPropagation()}>
                    {actions(item, index)}
                  </div>
                )}
              </div>

              <div className="responsive-table-card-body">
                {detailColumns.map(column => {
                  const value = column.renderCard
                    ? column.renderCard(item, index)
                    : column.render
                    ? column.render(item, index)
                    : (item as any)[column.key];

                  // V2.I.7: when header is a ReactNode (e.g. contains a
                  // sort indicator), fall back to cardLabel (plain string)
                  // for the mobile card label — cards expect flat text.
                  const cardLabel = column.cardLabel
                    ?? (typeof column.header === 'string' ? column.header : column.key);
                  return (
                    <div key={column.key} className="responsive-table-card-row">
                      <span className="responsive-table-card-label">{cardLabel}</span>
                      <span className="responsive-table-card-value">{value}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className={`responsive-table-container ${className}`}>
      <table className="responsive-table">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                onClick={column.onHeaderClick}
                aria-sort={column.onHeaderClick ? 'none' : undefined}
                style={{
                  textAlign: column.align || 'left',
                  width: column.width,
                  cursor: column.onHeaderClick ? 'pointer' : undefined,
                  userSelect: column.onHeaderClick ? 'none' : undefined,
                }}
              >
                {column.header}
              </th>
            ))}
            {actions && <th style={{ width: 'auto', textAlign: 'right' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={keyExtractor(item, index)}
              className={onRowClick ? 'clickable' : ''}
              onClick={() => onRowClick?.(item, index)}
            >
              {columns.map(column => (
                <td
                  key={column.key}
                  style={{ textAlign: column.align || 'left' }}
                >
                  {column.render
                    ? column.render(item, index)
                    : String((item as any)[column.key] ?? '')}
                </td>
              ))}
              {actions && (
                <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                  {actions(item, index)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ResponsiveTable;
